import {
    useCallback,
    useMemo,
    type FocusEvent,
    type KeyboardEvent,
    type MutableRefObject,
} from "react";
import { usePickerChain, usePickerLevel } from "./PickerChainContext";
import { usePickerQuery, usePickerRoot } from "./PickerContext";
import { collectItems, collectSelectableValues } from "./PickerTree";
import { firstPressableRow } from "./firstPressableRow";
import { preventBaseUIHandler } from "./usePickerLevelKeyboard";
import type {
    PickerItemData,
    PickerItemValue,
    PickerTypedCommitReason,
} from "./Picker.types";

interface UsePickerTypedCommitOptions<T extends PickerItemValue> {
    /** Only text triggers (input / cellInput without `allowCustomValue`) commit typed text. */
    isEnabled: boolean;
    isMultiple: boolean;
    items: ReadonlyArray<PickerItemData<T>> | undefined;
    /** Whether parent rows are values of their own (`selectableParents` after the cascade rule). */
    selectableParents: boolean;
    query: string;
    selectedValues: readonly T[];
    setSelectedValues: (values: readonly PickerItemValue[]) => void;
    setQuery: (text: string, reason?: string) => void;
    /** `null` shows the selection (its label on an input trigger, nothing otherwise). */
    resetQuery: (text: string | null) => void;
    closePopup: () => void;
    /**
     * Set by `usePickerBaseValue` (single-selection only) the instant a real
     * Base UI value change (a row press) commits, and consumed here on the
     * very next call — which for single-selection is guaranteed to be that
     * press's own stale blur echo, not a later user action. A row
     * press's own close sequence can still blur the trigger in the same
     * synchronous batch; that blur runs `commitTypedText` from a *stale*
     * closure — the one from before the press's state update re-rendered —
     * whose `query` is still the label the trigger was seeded with (the
     * previously selected item's name, on a cell reopened with a value
     * already set). Unguarded, that stale call matches the OLD label
     * against the picker's own items (a real catalog item, not typed junk)
     * and re-commits it, silently undoing the press the user just made
     * (PRO-12173: re-selecting a different item snaps back to the first).
     * The ref (not the closure) carries this across the stale call because
     * both hooks share the same mutable object.
     */
    suppressNextCommitRef: MutableRefObject<boolean>;
}

const normalize = (text: string) => text.trim().toLowerCase();

/**
 * Commit rule of a text trigger for typed text that is not a row press:
 * the unique selectable row whose label equals the text (case-insensitive,
 * trimmed) is committed through the root's selection, wherever it lives (a
 * collapsed tree branch, a flyout level that is not open). Single: Enter or
 * blur selects it and the input shows its label; without a match, blur
 * restores the selection's label (or empties the input). Multiple: Enter
 * toggles it; Enter and blur both leave the input empty for the summary.
 * Returns the callback, or `undefined` when the root has no text trigger.
 */
export const usePickerTypedCommit = <T extends PickerItemValue>({
    isEnabled,
    isMultiple,
    items,
    selectableParents,
    query,
    selectedValues,
    setSelectedValues,
    setQuery,
    resetQuery,
    closePopup,
    suppressNextCommitRef,
}: UsePickerTypedCommitOptions<T>) => {
    const candidates = useMemo(() => {
        const selectableValues = new Set<T>(
            collectSelectableValues({ items: items ?? [], selectableParents }),
        );
        return collectItems(items ?? []).filter(
            (item) =>
                item.disabled !== true && selectableValues.has(item.value),
        );
    }, [items, selectableParents]);

    const commitTypedText = useCallback(
        (reason: PickerTypedCommitReason): boolean => {
            // A row press just committed for real; this call is the stale
            // echo of that same press (see `suppressNextCommitRef`'s doc),
            // not a genuine typed-text commit. Consume the flag and no-op.
            if (suppressNextCommitRef.current) {
                suppressNextCommitRef.current = false;
                return false;
            }
            const needle = normalize(query);
            const matches =
                needle === ""
                    ? []
                    : candidates.filter(
                          (item) => normalize(item.label) === needle,
                      );
            const match = matches.length === 1 ? matches[0] : undefined;

            if (isMultiple) {
                if (match !== undefined && reason === "enter") {
                    setSelectedValues(
                        selectedValues.includes(match.value)
                            ? selectedValues.filter(
                                  (value) => value !== match.value,
                              )
                            : [...selectedValues, match.value],
                    );
                }
                if (query !== "") {
                    setQuery("", "input-change");
                }
                return match !== undefined && reason === "enter";
            }
            if (match !== undefined) {
                const isAlreadySelected =
                    selectedValues.length === 1 &&
                    selectedValues[0] === match.value;
                if (!isAlreadySelected) {
                    setSelectedValues([match.value]);
                }
                resetQuery(null);
                closePopup();
                return true;
            }
            if (reason === "blur") {
                resetQuery(null);
                closePopup();
            }
            return false;
        },
        [
            query,
            candidates,
            isMultiple,
            selectedValues,
            setSelectedValues,
            setQuery,
            resetQuery,
            closePopup,
            suppressNextCommitRef,
        ],
    );

    return isEnabled ? commitTypedText : undefined;
};

/**
 * The input-side half: Enter commits typed text when no row is highlighted
 * (a highlighted row stays Base UI's to select); blur commits unless focus
 * moved into a picker popup, which is a row press about to commit itself.
 * Under `autoHighlight`, Enter with a query and no highlight presses the
 * first enabled value row: the keystroke that opens the popup comes before its
 * portalled rows mount, so Base UI's own first-row highlight only lands from
 * the next keystroke.
 */
export const usePickerTypedCommitHandlers = () => {
    const root = usePickerRoot();
    const level = usePickerLevel();
    const chain = usePickerChain();
    const { commitTypedText, searchText } = usePickerQuery();
    const isAutoHighlight = chain?.isAutoHighlight === true;

    const onKeyDown = useCallback(
        (event: KeyboardEvent<HTMLInputElement>) => {
            const popup = level?.popupRef.current;
            if (
                commitTypedText === undefined ||
                event.key !== "Enter" ||
                event.defaultPrevented ||
                popup?.querySelector("[data-highlighted]")
            ) {
                return;
            }
            const firstRow = firstPressableRow(popup);
            if (isAutoHighlight && searchText.trim() !== "" && firstRow) {
                event.preventDefault();
                preventBaseUIHandler(event);
                firstRow.click();
                return;
            }
            if (commitTypedText("enter")) {
                event.preventDefault();
                preventBaseUIHandler(event);
            }
        },
        [commitTypedText, level, isAutoHighlight, searchText],
    );
    const onBlur = useCallback(
        (event: FocusEvent<HTMLInputElement>) => {
            const { relatedTarget } = event;
            const isInsidePicker =
                relatedTarget instanceof Element &&
                (relatedTarget.closest("[data-picker-popup]") !== null ||
                    root.triggerAnchor?.contains(relatedTarget) === true);
            if (commitTypedText === undefined || isInsidePicker) {
                return;
            }
            commitTypedText("blur");
        },
        [commitTypedText, root.triggerAnchor],
    );

    return { onKeyDown, onBlur };
};
